import React from "react";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import {
  CodeOutlined,
  CopyAllOutlined,
  FiberManualRecord,
  RestartAlt,
  SpeedOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { IconButton, useTheme } from "@mui/material";

import { useAPICredentialDetailContext } from "contexts/APICredentialDetailContext";

import EmptyResult from "components/Shared/Lottie/EmptyResult";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";

import {
  useCreateApiCredentialMutation,
  useUpdateApiCredentialMutation,
} from "store/services/apiCredentialApi";

import { localizeDateShort } from "utils/dateFormats";

const CredentialDetail = () => {
  const { credential, loadingCredential, refetch } =
    useAPICredentialDetailContext();

  const [
    createApiCredential,
    { isLoading: creatingCredential, isFetching: fetchingCredential },
  ] = useCreateApiCredentialMutation();

  // methods
  const handleGenerateCredential = async () => {
    try {
      await createApiCredential().unwrap();
      toast.success("Credential Successfully created!");
      refetch();
    } catch (err) {
      console.log(err);

      toast.error(err?.data?.message ?? "Couldn't create Credential");
    }
  };

  if (loadingCredential || fetchingCredential) return <InfoGridSkeleton />;

  if (!credential)
    return (
      <div className="grid place-content-center pb-4">
        <EmptyResult height={100} label="Credentials not initialized!" />

        <LoadingButton
          loading={creatingCredential}
          onClick={handleGenerateCredential}
        >
          Generate Credential
        </LoadingButton>
      </div>
    );

  return (
    <div className="grid gap-6 p-5">
      <div className="text-sm text-gray-600">
        This API credential can be used to integrate our services with your
        system. Please keep your API key secure and do not share it with others.
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* POST Usage Card */}
        <div className="grid gap-2 rounded border p-3">
          <div className="flex items-center gap-3">
            <CodeOutlined fontSize="small" color="primary" />
            <div className="text-sm font-medium">POST Requests</div>
          </div>
          <div className="text-xl">
            {credential?.quotas?.POST?.currentUsage || 0} / {credential?.quotas?.POST?.limit || 0}
          </div>
          <div className="text-xs">
            {credential?.quotas?.POST?.limit - (credential?.quotas?.POST?.currentUsage || 0)} remaining
          </div>
        </div>

        {/* GET Usage Card */}
        <div className="grid gap-2 rounded border p-3">
          <div className="flex items-center gap-3">
            <CodeOutlined fontSize="small" color="success" />
            <div className="text-sm font-medium">GET Requests</div>
          </div>
          <div className="text-xl">
            {credential?.quotas?.GET?.currentUsage || 0} / {credential?.quotas?.GET?.limit || 0}
          </div>
          <div className="text-xs">
            {credential?.quotas?.GET?.limit - (credential?.quotas?.GET?.currentUsage || 0)} remaining
          </div>
        </div>

        {/* Keep existing Resets on card */}
        <div className="grid gap-2 rounded border p-3">
          <div className="flex items-center gap-3">
            <SpeedOutlined fontSize="small" color="primary" />
            <div className="text-sm font-medium">Resets on</div>
          </div>
          <div className="text-xl">
            {credential?.resetDate && new Date(credential.resetDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })} at {credential?.resetDate && new Date(credential.resetDate).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        </div>
      </div>

      <ApiKey credential={credential} />

      <SecretKey credential={credential} />
    </div>
  );
};

export default CredentialDetail;

const Key = ({
  label,
  value = "",
  loading = false,
  onRegenerate = () => { },
}) => {
  const theme = useTheme();
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="flex items-center gap-6">
      <div
        style={{
          width: 128,
          fontSize: 14,
          fontWeight: 500,
          color: theme.palette.text.secondary,
        }}
      >
        {label}
      </div>

      <div
        style={{
          display: "flex",
          flexGrow: 1,
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          padding: "4px 8px 4px 16px",
        }}
      >
        <div
          style={{
            letterSpacing: "0.2em",
            fontFamily: "monospace",
            color: theme.palette.text.primary,
          }}
        >
          {value?.split("")?.map((char, idx) =>
            visible ? (
              <span key={idx}>{char}</span>
            ) : (
              <FiberManualRecord
                key={idx}
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.disabled,
                }}
              />
            ),
          )}
        </div>

        <div>
          <IconButton
            onClick={() => setVisible((prev) => !prev)}
            sx={{ color: theme.palette.text.secondary }}
          >
            {!visible ? (
              <VisibilityOutlined color="primary" />
            ) : (
              <VisibilityOffOutlined />
            )}
          </IconButton>

          <IconButton
            onClick={() => {
              navigator.clipboard.writeText(value);
              toast.success("API Key copied to clipboard!");
            }}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CopyAllOutlined fontSize="small" />
          </IconButton>
        </div>
      </div>

      <LoadingButton
        loading={loading}
        variant="outlined"
        startIcon={<RestartAlt />}
        onClick={onRegenerate}
        sx={{
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }}
      >
        Regenerate
      </LoadingButton>
    </div>
  );
};

const ApiKey = ({ credential = null }) => {
  const [updateApiCredential, { isLoading: updatingCredential }] =
    useUpdateApiCredentialMutation();

  // methods
  const handleRegenerateCredential = React.useCallback(async () => {
    try {
      await updateApiCredential({
        credentialId: credential?._id,
        regenerateKey: true,
      }).unwrap();
      toast.success("Credential Successfully updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update Credential");
    }
  }, [credential?._id, updateApiCredential]);

  return (
    <Key
      label="API Key"
      value={credential?.key}
      loading={updatingCredential}
      onRegenerate={handleRegenerateCredential}
    />
  );
};

const SecretKey = ({ credential = null }) => {
  const [updateSecretKey, { isLoading: updatingCredential }] =
    useUpdateApiCredentialMutation();

  // methods
  const handleRegenerateSecret = React.useCallback(async () => {
    try {
      await updateSecretKey({
        credentialId: credential?._id,
        regenerateSecretKey: true,
      }).unwrap();
      toast.success("Credential Successfully updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update Credential");
    }
  }, [credential?._id, updateSecretKey]);

  return (
    <Key
      label="Secret Key"
      value={credential?.secret}
      loading={updatingCredential}
      onRegenerate={handleRegenerateSecret}
    />
  );
};
